/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {ObjectUtils} from "../../../../Utils";
import {usePermission} from "../../../../Hooks/PermissionHooks";
import {buildAnswer} from "../../../../Hooks/SurveyFormHooks";
import {HelpIcon} from "../../HelpIcon";

const DEFAULT_STATE = {
    yes: {title: "Submitted",         message: null, linkUrl: null, linkText: null},
    no:  {title: "Not yet submitted", message: null, linkUrl: null, linkText: null},
};

/**
 * Survey input for a yes/no prerequisite typically set by an external system.
 *
 * Read-only by default; editable only by users with the permission named in
 * options.requiresPermission (for testing/override purposes). The backend
 * enforces the same permission gate during answer save — the frontend gate
 * is for UX, the backend gate is the source of truth.
 *
 * Completion semantics differ from `bool`:
 *   valueBool === 1  → complete
 *   valueBool === 0  → incomplete (treated identically to null)
 *   valueBool null   → incomplete
 *
 * Configure per-question via SurveyQuestion.options JSON. See
 * documentation/Specifications/Survey Question Data Types.md for the full schema.
 */
export const SurveyFormBoolPrerequisite = (props) => {
    const {
        ConservationAction: conservationAction,
        Question: question,
        Answer: answer,
        OnChange: onChange = () => {},
        EditMode,
    } = props;

    const hasPermissions = usePermission();

    const rawOptions = question?.options ?? {};
    const options = ObjectUtils.IsString(rawOptions) ? JSON.parse(rawOptions) : rawOptions;

    const requiredPermission = options?.requiresPermission ?? null;
    const canEdit = EditMode && (!requiredPermission || hasPermissions(requiredPermission));

    const isComplete = answer?.valueBool === 1 || answer?.valueBool === true;
    const stateKey = isComplete ? "yes" : "no";
    const state = {...DEFAULT_STATE[stateKey], ...(options?.[`${stateKey}State`] ?? {})};

    // Substitute {projectId} in the link URL with the current project's ID.
    // Simple single-token substitution rather than a full templating system —
    // generalize only if more tokens are needed in the future.
    const linkUrl = state.linkUrl?.replace(/\{projectId\}/g, conservationAction?.projectId ?? "");

    // Setting back to null (not 0) when unchecked avoids creating a spurious
    // SurveyAnswer change for questions that were previously unanswered.
    const handleToggle = (event) => {
        onChange(buildAnswer(question, conservationAction, answer, {
            valueBool: event.target.checked ? 1 : null,
        }));
    };

    const icon = isComplete
        ? <FontAwesomeIcon icon={["fas", "check-circle"]} className="text-success" fixedWidth />
        : <FontAwesomeIcon icon={["fal", "times-circle"]} className="text-muted"   fixedWidth />;

    return (
        <div className={`sb-survey-bool-prerequisite${isComplete ? " is-complete" : " is-incomplete"}`}>
            <div className="sb-survey-bool-prerequisite-status">
                <span className="sb-survey-bool-prerequisite-icon">{icon}</span>
                <div className="sb-survey-bool-prerequisite-body">
                    <strong className="sb-survey-bool-prerequisite-title">{state.title}</strong>
                    {state.message && (
                        <div className="sb-survey-bool-prerequisite-message">{state.message}</div>
                    )}
                    {linkUrl && (
                        <a
                            className="sb-survey-bool-prerequisite-link"
                            href={linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {state.linkText ?? linkUrl}
                        </a>
                    )}
                </div>
            </div>
            {canEdit && (
                <label className="sb-survey-bool-prerequisite-override">
                    <input
                        type="checkbox"
                        checked={isComplete}
                        onChange={handleToggle}
                    />
                    <span>Mark as complete</span>
                    <HelpIcon id={`survey-bool-prerequisite-help-${question.surveyQuestionId}`}>
                        Only editable by TFT reviewers. This value is normally set by an external system.
                    </HelpIcon>
                </label>
            )}
        </div>
    );
};
